import { ChangeDetectionStrategy, Component, computed, inject, OnDestroy, signal } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { NZ_MODAL_DATA, NzModalRef } from 'ng-zorro-antd/modal';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzTooltipModule } from 'ng-zorro-antd/tooltip';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { toSignal } from '@angular/core/rxjs-interop';
import { finalize } from 'rxjs/operators';
import { timer } from 'rxjs';
import { SessionService } from '@core/session/store/session.service';
import { User } from '@core/session/models/user';
import { LoginMode } from '@core/session/components/login-modal/login-modal.component';
import { LoginFailedError } from '@backend/api/session/session-api.errors';
import { InternalServerError } from '@core/http/models/internal-server-error';
import { GlobalAppApi } from '@features/global/api/global-app.api';
import { SessionQuery } from '@core/session/store/session.query';

/** Сколько окно показывает, кем вошёл, прежде чем закрыться. */
const WELCOME_DURATION_MS = 1200;

/**
 * Окно входа в новом дизайне.
 *
 * Отличий от старого три, и все они про ответ интерфейса на действие:
 *
 * 1. Режимы `login` и `timeout` больше не выглядят одинаково. Старое окно не читало
 *    `loginMode` вовсе, и человек, которого выкинуло посреди смены, видел ту же
 *    «Авторизацию», что и при первом входе — не понимая, цел ли незаписанный параметр.
 * 2. Результат возвращается в окно, а не во всплывающее уведомление в углу: ошибка сервера
 *    показывается над формой, ошибка заполнения — под своим полем.
 * 3. Повторный Enter во время запроса больше не отправляет вторую попытку.
 *
 * Старое окно остаётся рядом и работает при выключенном флаге дизайна.
 */
@UntilDestroy()
@Component({
  selector: 'rais-login-modal-v2',
  imports: [ReactiveFormsModule, NzIconModule, NzTooltipModule],
  template: `
    <div class="head" [class.head--with-org]="organization()">
      <img class="head__logo" src="assets/img/app_logo.svg" alt="РАИС" />
      <b class="head__title">{{ isTimeout() ? 'Сессия завершена' : 'Вход в систему' }}</b>
    </div>
    @if (organization(); as organization) {
      <p class="org" data-testid="login-organization">{{ organization }}</p>
    }

    @if (isTimeout()) {
      <p class="timeout-note">
        Система заблокирована после 5 минут без действий. Введённые, но не сохранённые данные остались на экране за
        окном.
      </p>
    }

    @if (welcomeUser(); as user) {
      <div class="done">
        <span class="done__mark" nz-icon nzType="check"></span>
        <span class="done__text">
          <b>{{ fullName(user) }}</b>
          <span>{{ userPlace(user) }}</span>
        </span>
      </div>
    } @else {
      <form class="form" [formGroup]="form" (ngSubmit)="submit()">
        @if (serverError(); as error) {
          <div class="alert" role="alert">
            <span class="alert__mark" nz-icon nzType="close-circle" nzTheme="fill"></span>
            <div class="alert__text">
              <b>{{ error.title }}</b>
              <span>{{ error.detail }}</span>
              @if (error.requestId; as requestId) {
                <span class="reqid">
                  <code>{{ requestId }}</code>
                  <button type="button" class="reqid__copy" (click)="copyRequestId(requestId)">
                    {{ requestIdCopied() ? 'Скопировано' : 'Скопировать' }}
                  </button>
                </span>
              }
            </div>
          </div>
        }

        <label class="field">
          <span class="field__label">Имя пользователя</span>
          <input
            class="control"
            type="text"
            formControlName="login"
            autocomplete="username"
            data-testid="login-input"
            [class.control--error]="loginInvalid()"
            [attr.aria-invalid]="loginInvalid()"
          />
          @if (loginInvalid()) {
            <span class="field__msg">Введите имя пользователя</span>
          }
        </label>

        <label class="field">
          <span class="field__label">Пароль</span>
          <span class="control control--with-suffix" [class.control--error]="passwordInvalid()">
            <input
              [type]="showPassword() ? 'text' : 'password'"
              formControlName="password"
              autocomplete="current-password"
              data-testid="password-input"
              (keyup)="detectCapsLock($event)"
              (keydown)="detectCapsLock($event)"
            />
            <button
              type="button"
              tabindex="-1"
              class="control__eye"
              nz-tooltip
              [nzTooltipTitle]="showPassword() ? 'Скрыть пароль' : 'Показать пароль'"
              (click)="togglePasswordVisibility()"
            >
              <span nz-icon [nzType]="showPassword() ? 'eye' : 'eye-invisible'"></span>
            </button>
          </span>
          @if (passwordInvalid()) {
            <span class="field__msg">Введите пароль</span>
          } @else if (capsLockOn()) {
            <span class="field__hint">
              <span nz-icon nzType="arrow-up"></span>
              Включён Caps Lock
            </span>
          }
        </label>

        <button
          type="submit"
          class="submit"
          data-testid="login-btn"
          [disabled]="isLoggingIn()"
          [attr.aria-busy]="isLoggingIn()"
        >
          @if (isLoggingIn()) {
            <span class="submit__spinner" nz-icon nzType="loading"></span>
            Проверяем учётные данные…
          } @else {
            {{ isTimeout() ? 'Продолжить работу' : 'Войти' }}
          }
        </button>
      </form>
    }
  `,
  styleUrl: './login-modal-v2.component.less',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LoginModalV2Component implements OnDestroy {
  private modalData = inject<{ submittedCallback: (user: User) => void; loginMode: LoginMode }>(NZ_MODAL_DATA);
  private modalRef = inject(NzModalRef);
  private sessionService = inject(SessionService);
  private globalAppApi = inject(GlobalAppApi);
  private sessionQuery = inject(SessionQuery);

  /** Тот же флаг, что и у старого окна: пока открыто одно, второе не открывается. */
  static LoginModalOpened: boolean;

  protected readonly form = new FormGroup({
    login: new FormControl('', { nonNullable: true, validators: Validators.required }),
    password: new FormControl('', { nonNullable: true, validators: Validators.required }),
  });

  protected readonly isLoggingIn = signal(false);
  protected readonly showPassword = signal(false);
  protected readonly capsLockOn = signal(false);
  protected readonly organization = signal<string | null>(null);
  protected readonly welcomeUser = signal<User | null>(null);
  protected readonly requestIdCopied = signal(false);
  protected readonly serverError = signal<{ title: string; detail: string; requestId: string | null } | null>(null);

  protected readonly isTimeout = signal(this.modalData.loginMode === 'timeout');

  /**
   * Приложение зонлесс, а реактивная форма о своих изменениях сигналами не сообщает: без этого
   * `markAllAsTouched()` менял состояние контролов, но окно не перерисовывалось и ошибки не
   * появлялись. Статус формы заводится в сигнал, и разметка ошибок считается из него.
   */
  private readonly formStatus = toSignal(this.form.statusChanges, { initialValue: this.form.status });
  private readonly submitAttempted = signal(false);

  protected readonly loginInvalid = computed(() => this.isFieldInvalid('login'));
  protected readonly passwordInvalid = computed(() => this.isFieldInvalid('password'));

  constructor() {
    LoginModalV2Component.LoginModalOpened = true;

    // Название организации доступно до входа через публичный api/public/global-settings/organization-name
    // (остальные глобальные настройки требуют сессию). Не загрузилось — строки просто нет.
    this.globalAppApi
      .getPublicOrganization()
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (response) => this.organization.set(response?.name || null),
        error: () => this.organization.set(null),
      });

    // В режиме timeout меняется только пароль: кто работает — известно.
    const currentLogin = this.sessionQuery.user?.login;
    if (this.isTimeout() && currentLogin) {
      this.form.controls.login.setValue(currentLogin);
    }

    this.form.controls.password.valueChanges.pipe(untilDestroyed(this)).subscribe((value) => {
      if (!value) {
        this.showPassword.set(false);
      }
    });
  }

  ngOnDestroy(): void {
    LoginModalV2Component.LoginModalOpened = false;
  }

  protected togglePasswordVisibility(): void {
    this.showPassword.update((visible) => !visible);
  }

  private isFieldInvalid(field: 'login' | 'password'): boolean {
    this.formStatus();
    const control = this.form.controls[field];
    return control.invalid && (this.submitAttempted() || control.dirty);
  }

  protected detectCapsLock(event: KeyboardEvent): void {
    this.capsLockOn.set(event.getModifierState?.('CapsLock') ?? false);
  }

  protected fullName(user: User): string {
    return [user.lastName, user.firstName, user.patronymicName].filter(Boolean).join(' ');
  }

  protected userPlace(user: User): string {
    return [user.position, user.department?.name, user.room?.name].filter(Boolean).join(' · ');
  }

  protected copyRequestId(requestId: string): void {
    navigator.clipboard
      ?.writeText(requestId)
      .then(() => this.requestIdCopied.set(true))
      .catch(() => this.requestIdCopied.set(false));
  }

  protected submit(): void {
    // Enter во время запроса ничего не делает: раньше он слал новую попытку на каждое нажатие,
    // потому что isLoggingIn гасил только кнопку.
    if (this.isLoggingIn()) {
      return;
    }

    this.submitAttempted.set(true);
    this.form.markAllAsTouched();
    if (this.form.invalid) {
      return;
    }

    this.serverError.set(null);
    this.isLoggingIn.set(true);

    this.sessionService
      .auth(this.form.getRawValue())
      .pipe(
        finalize(() => this.isLoggingIn.set(false)),
        untilDestroyed(this),
      )
      .subscribe({
        next: (session) => this.showWelcomeAndClose(session.user),
        error: (error) => this.showError(error),
      });
  }

  /**
   * Успех показывается в самом окне вместо уведомления на 9999 мс. Важен не факт входа —
   * он и так виден по закрытию окна, — а под чьей учётной записью: на посту один компьютер
   * на смену, и работа под чужим логином реальная ошибка.
   */
  private showWelcomeAndClose(user: User): void {
    this.welcomeUser.set(user);

    timer(WELCOME_DURATION_MS)
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        this.modalData.submittedCallback(user);
        this.modalRef.close();
      });
  }

  private showError(error: unknown): void {
    this.requestIdCopied.set(false);

    if (error instanceof LoginFailedError) {
      // Текст приходит от сервера и уже различает случаи: неверный пароль, нет такого
      // пользователя, пользователь заблокирован. Раскладывать его по полям на клиенте
      // нечем — кода ошибки бэкенд не отдаёт, поэтому показываем как есть.
      this.serverError.set({
        title: error.message,
        detail: 'Проверьте раскладку и Caps Lock. Если данные верны, обратитесь к администратору.',
        requestId: null,
      });
      // Пароль очищаем, но не помечаем как «не заполнен»: его стёрла система, а не пользователь.
      // Иначе поверх ответа сервера появлялось второе сообщение — «Введите пароль».
      this.form.controls.password.reset();
      this.submitAttempted.set(false);
      return;
    }

    if (error instanceof InternalServerError) {
      this.serverError.set({
        title: 'Ошибка на сервере',
        detail: 'Войти не удалось. Повторите попытку; если не помогает, передайте в поддержку номер запроса.',
        requestId: error.requestId,
      });
      return;
    }

    this.serverError.set({
      title: 'Сервер недоступен',
      detail: 'Проверьте сеть и повторите попытку. Номера запроса нет: сервер не ответил.',
      requestId: null,
    });
  }
}
